import clsx from "clsx";
import { MdArrowDownward, MdArrowUpward } from "react-icons/md";
import moment from "moment";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useState } from "react";

import {
  formatNumber,
  getTotalByTransactionType,
  rgbaObjectToString,
} from "../../helpers/utils";
import { ETransactionType, ICON_SIZES } from "../../helpers/constants";
import useCurrentUser from "../../hooks/useCurrentUser";
import useCurrentAccount from "../../hooks/useCurrentAccount";
import useMobile from "src/hooks/useMobile";

const TodayTotalsBar: React.FC = () => {
  const currentLanguage = i18next.language?.toLowerCase?.() || "en";
  const { isLoadingCurrentAccount, todayTransactions, currencySymbol } =
    useCurrentAccount();
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const { colors } = useCurrentUser();

  const [isHidden, setHidden] = useState(false);

  const income = getTotalByTransactionType({
    transactions: todayTransactions,
    type: ETransactionType.INCOME,
  });
  const expenses = getTotalByTransactionType({
    transactions: todayTransactions,
    type: ETransactionType.EXPENSE,
  });

  const formattedDate = () => {
    const m = moment().locale(currentLanguage);
    const dayName = m.format("dddd");
    const dayNumber = m.format("DD");
    const monthName = m.format("MMMM");
    const _yearNumber = m.format("YYYY");

    const capitalize = (str: string) =>
      str.charAt(0).toUpperCase() + str.slice(1);

    return `${capitalize(dayName)} ${dayNumber}`;
  };

  if (isLoadingCurrentAccount) return null;

  return (
    <div
      className={clsx([
        "flex",
        "justify-center",
        "w-full",
        "sticky",
        "top-[calc(132px+env(safe-area-inset-top))]",
        "md:top-[calc(90px+env(safe-area-inset-top))]",
        "-mt-5",
        "md:mt-0",
        "transition-opacity",
        "duration-300",
        "pointer-events-none",
        isHidden ? "opacity-0" : "opacity-100",
      ])}
      style={{ zIndex: 19 }}
    >
      <ul
        className={clsx([
          "rounded-full",
          "bg-blue-900/90",
          "dark:bg-gray-900/90",
          "md:dark:bg-gray-900/90",
          "text-white",
          "md:dark:text-gray-300",
          "text-sm",
          "md:text-sm",
          "px-3",
          "py-1",
          "inline-flex",
          "flex-row",
          "gap-1",
          "justify-center",
          "shadow-md",
          "font-normal",
        ])}
      >
        <li className={clsx(["flex", "flex-row", "items-center"])}>
          <div className={clsx(["text-sm", "md:text-sm"])}>
            {formattedDate()}
          </div>
        </li>

        {income === 0 && expenses === 0 ? (
          <li className={clsx(["flex", "flex-row", "gap-1", "items-center"])}>
            <MdArrowDownward
              className="rounded-sm"
              style={{
                color: rgbaObjectToString(colors?.transactions?.income?.color),
                background: rgbaObjectToString(
                  colors?.transactions?.income?.bg
                ),
              }}
              size={ICON_SIZES.XS}
            />
            <MdArrowUpward
              className="rounded-sm"
              style={{
                color: rgbaObjectToString(
                  colors?.transactions?.expenses?.color
                ),
                background: rgbaObjectToString(
                  colors?.transactions?.expenses?.bg
                ),
              }}
              size={ICON_SIZES.XS}
            />
            <div className={clsx(["text-sm", "md:text-sm"])}>
              {t("shared.no_movements")}
            </div>
          </li>
        ) : (
          <>
            {income > 0 && (
              <li
                className={clsx(["flex", "flex-row", "gap-1", "items-center"])}
              >
                <MdArrowDownward
                  style={{
                    color: rgbaObjectToString(
                      colors?.transactions?.income?.color
                    ),
                    background: rgbaObjectToString(
                      colors?.transactions?.income?.bg
                    ),
                  }}
                  size={ICON_SIZES.XS}
                  className="rounded-sm"
                />
                <div className={clsx(["text-sm", "md:text-sm"])}>
                  {currencySymbol}
                  {formatNumber(income)}
                </div>
              </li>
            )}

            {expenses > 0 && (
              <li
                className={clsx(["flex", "flex-row", "gap-1", "items-center"])}
              >
                <MdArrowUpward
                  style={{
                    color: rgbaObjectToString(
                      colors?.transactions?.expenses?.color
                    ),
                    background: rgbaObjectToString(
                      colors?.transactions?.expenses?.bg
                    ),
                  }}
                  size={ICON_SIZES.XS}
                  className="rounded-sm"
                />
                <div className={clsx(["text-sm", "md:text-sm"])}>
                  {currencySymbol}
                  {formatNumber(expenses)}
                </div>
              </li>
            )}
          </>
        )}
      </ul>
    </div>
  );
};

export default TodayTotalsBar;
